// A proxy class between checkout controls and CmsDeliverySelector
import { CmsDeliverySelector, IDeliverySelectorInputData, IDeliveryOptionWithPassThroughData } from "./cms-delivery-selector.js";
import { ICmsDeliverySelectorConfiguration } from "./interfaces/cms-delivery-selector-config-interface.js";
import {show, hide} from "../../utils/dom_visibility.js"

interface IAddress {
    countryCode: string;
    postalCode: string;
    isDeliverable: boolean;
}

interface ISettings {
    $container: any;
    controlId: number;
    R: string;
    checkoutStepNumber: number;
    preferredDeliveryMethodId: string;
    onDeliveryOptionsAvailable: {(): void};
    onDeliveryOptionSelected: { (option: IDeliveryOptionWithPassThroughData): void};
    onNoDeliveryOptionAvailable: { (msg: string, initiator: string): void };
    blockUi: {(): void};
    unBlockUi: {(): void}
}

class CmsDeliveryOptionsController {
    missingInputsMsg: string = 'missingInputs';
    undeliverableMsg: string = 'undeliverable';
    settings: ISettings;
    container: HTMLElement;
    context: any;
    selectedOption: IDeliveryOptionWithPassThroughData;
    deliveryAddress: IAddress;
    deliveryDate: string = null;
    deliverySelector: CmsDeliverySelector;
    deliverySelectorConfig: ICmsDeliverySelectorConfiguration;

    constructor(context: any) {
        this.context = context;

        this.deliverySelectorConfig =
            JSON.parse(this.context.deliverySelectorConfig) as ICmsDeliverySelectorConfiguration;
    }

    public init(settings: any) {
        this.setSettings(settings);

        this.container = settings.$container[0];

        this.initDeliverySelector();
    }

    public async setDeliveryDate(deliveryDate: string, initiator: string) {
        if (this.deliveryDate === null && deliveryDate === null) {
            return;
        }

        await this.checkAddressAndReloadIfNeeded(this.deliveryAddress, false, initiator, deliveryDate);
    }

    public async setDeliveryAddress(address: IAddress, initiator: string) {
        const a: IAddress = { ...address }; // create a copy in case external object changes later
        await this.checkAddressAndReloadIfNeeded(a, false, initiator, this.deliveryDate);
    }

    public async reloadDeliveryOptions(initiator: string) { // call when something other than address has changed and can affect delivery availability or price
        await this.checkAddressAndReloadIfNeeded(this.deliveryAddress, true, initiator, this.deliveryDate);
    }

    private async checkAddressAndReloadIfNeeded(address: IAddress, forceReload: boolean, initiator: string, deliveryDate: string) {
        let addressErrorMsg: string = null;
        if (this.isRequiredDataMissing(address)) {
            addressErrorMsg = this.missingInputsMsg;
        } else if (!address.isDeliverable) {
            addressErrorMsg = this.undeliverableMsg;
        }

        if (addressErrorMsg) {
            this.selectedOption = null;
            hide(this.container);
            if (typeof this.settings.onNoDeliveryOptionAvailable === 'function') {
                this.settings.onNoDeliveryOptionAvailable(addressErrorMsg, initiator);
            }
        } else if (forceReload || this.shouldReload(address, deliveryDate)) {
            this.deliveryAddress = address;
            this.deliveryDate = deliveryDate;
            await this.updateDeliverySelector(address, initiator, deliveryDate);
        } else {
            this.notifyDeliveryOptionSelected(false);
        }
    }

    private isRequiredDataMissing(address: IAddress) {
        if (!address) {
            return true;
        }

        if (!address.countryCode) {
            return true;
        }
        if (!address.postalCode && this.context.deliveryDependsOnPostalCode) {
            return true;
        }

        return false;
    }

    private shouldReload(address: IAddress, deliveryDate: string) {
        return !this.deliveryAddress
            || address.countryCode !== this.deliveryAddress.countryCode
            || (this.context.deliveryDependsOnPostalCode
                && address.postalCode !== this.deliveryAddress.postalCode)
            || deliveryDate !== this.deliveryDate;
    }

    private async updateDeliverySelector(address: IAddress, initiator: string, deliveryDate: string) {
        this.blockUI();

        const inputData = {
            countryCode: address.countryCode,
            postalCode: address.postalCode,
            deliveryDate: deliveryDate
        } as IDeliverySelectorInputData;

        await this.deliverySelector.update(inputData).then(() => {
            this.unBlockUI();
            show(this.container);

            if (typeof this.settings.onDeliveryOptionsAvailable === 'function') {
                this.settings.onDeliveryOptionsAvailable();
            }
        }).catch(responseError => {
            hide(this.container);
            this.unBlockUI();
            
            if (typeof this.settings.onNoDeliveryOptionAvailable === 'function') {
                this.settings.onNoDeliveryOptionAvailable(responseError.errorMessage, initiator);
            }

            throw responseError;
        });
    }

    private notifyDeliveryOptionSelected(optionIdChanged: boolean) {
        if (!this.selectedOption || this.selectedOption.deliveryID < 1) {
            return;
        }
        
        if (optionIdChanged) {
            const event = new CustomEvent("deliveryChanged", { detail: [this.selectedOption.deliveryID, this.selectedOption] } as unknown as CustomEventInit);
            document.dispatchEvent(event);

            const optionName = this.selectedOption.name;
            (window as any).reportDeliveryOptionSelection(this.settings.checkoutStepNumber, optionName);
        }

        if (typeof this.settings.onDeliveryOptionSelected === 'function') {
            this.settings.onDeliveryOptionSelected(this.selectedOption);
        }
    }

    private setSettings(settings: ISettings) {
        this.settings = Object.assign({},
            {
                $container: null,
                controlId: null,
                R: null,
                checkoutStepNumber: 4,
                preferredDeliveryMethodId: null,
                blockUi() { },
                unBlockUi() { },
                onDeliveryOptionsAvailable() { },
                onDeliveryOptionSelected(selectedOption: IDeliveryOptionWithPassThroughData) { },
                onNoDeliveryOptionAvailable(reasonMsg: string, initiator: string) { } // used to display unavailable delivery error message and disable checkout
            },
            settings);
    }

    private initDeliverySelector() {
        if (this.deliverySelector) {
            return this.deliverySelector;
        }

        this.deliverySelector = new CmsDeliverySelector(this.deliverySelectorConfig);

        this.container.appendChild(this.deliverySelector);

        this.deliverySelector.onSelectedOptionChanged(option => {
            const changed = !this.selectedOption || option.id !== this.selectedOption.id;
            this.selectedOption = option;
            this.notifyDeliveryOptionSelected(changed);
        });

        this.deliverySelector.onOptionPropertyChanged(option => {
            if (option.id === this.selectedOption.id) {
                this.selectedOption = option;
                this.notifyDeliveryOptionSelected(false);
            }
        });
    }

    private blockUI() {
        if (this.settings && typeof this.settings.blockUi === "function") {
            this.settings.blockUi();
        }
    }

    private unBlockUI() {
        if (this.settings && typeof this.settings.unBlockUi === "function") {
            this.settings.unBlockUi();
        }
    }
}

((w: any) => {
    const context = w.umwAssets["deliveryOptionsControllerV2"][0];
    w.deliveryOptionsController = new CmsDeliveryOptionsController(context);
})(window as any);

